/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

/**
 * Coercion helpers between the boxed `Any?` values a [FieldBase] stores and the
 * concrete numeric types the protocol works in.
 *
 * FIT is self-describing, so a field's runtime type is only known from the profile
 * or from the message definition in the file. Values are therefore kept boxed and
 * narrowed at the API boundary by the generated typed accessors.
 */
internal object FitValues {
    /**
     * Widens any FIT numeric value to [Double].
     *
     * Returns [Double.NaN] for a value that is not numeric, which every caller
     * treats as invalid.
     */
    fun toDouble(value: Any?): Double =
        when (value) {
            null -> Double.NaN
            is Double -> value
            is Float -> value.toDouble()
            is UByte -> value.toDouble()
            is UShort -> value.toDouble()
            is UInt -> value.toDouble()
            is ULong -> value.toDouble()
            is Byte -> value.toDouble()
            is Short -> value.toDouble()
            is Int -> value.toDouble()
            is Long -> value.toDouble()
            is Boolean -> if (value) 1.0 else 0.0
            else -> Double.NaN
        }

    /**
     * Narrows any FIT numeric value to [Long] without going through [Double].
     *
     * Bit-exact for 64-bit values, which [toDouble] is not: a Double has 53 bits of
     * mantissa, so routing a UINT64 through it silently rounds.
     */
    fun toLong(value: Any?): Long =
        when (value) {
            null -> 0L
            is Long -> value
            is ULong -> value.toLong()
            is UByte -> value.toLong()
            is UShort -> value.toLong()
            is UInt -> value.toLong()
            is Byte -> value.toLong()
            is Short -> value.toLong()
            is Int -> value.toLong()
            is Double -> value.toLong()
            is Float -> value.toLong()
            is Boolean -> if (value) 1L else 0L
            else -> 0L
        }

    /** Renders a value the way the FIT profile names it, for diagnostics. */
    fun toStringValue(value: Any?): String =
        when (value) {
            null -> ""
            is String -> value
            else -> value.toString()
        }

    /** The raw bit pattern of a numeric value, sized to its base type. */
    fun rawBits(value: Any?): ULong =
        when (value) {
            null -> 0uL
            is UByte -> value.toULong()
            is UShort -> value.toULong()
            is UInt -> value.toULong()
            is ULong -> value
            is Byte -> value.toUByte().toULong()
            is Short -> value.toUShort().toULong()
            is Int -> value.toUInt().toULong()
            is Long -> value.toULong()
            is Float -> value.toRawBits().toUInt().toULong()
            is Double -> value.toRawBits().toULong()
            else -> 0uL
        }
}
